/** Reading a task's figures, and replacing the arrangement and the definitions they are read in. */

import { get, post, put } from "@/services/api-client";
import type {
  FigureBriefRunResponse,
  FigureDefinitionsRequest,
  FigureDefinitionsResponse,
  FigureDrawingResponse,
  FigureLayoutRequest,
  FigureLayoutResponse,
  FigureListResponse,
  FigureReviewAction,
  FigureReviewState,
} from "@patent-studio/api-types";

export function listFigures(taskId: string): Promise<FigureListResponse> {
  return get<FigureListResponse>(`/tasks/${taskId}/figures`);
}

/** Ask Praxis for one figure's Brief Figure Description, which replaces the one there when it lands. */
export function describeFigure(
  taskId: string,
  fileId: string,
): Promise<FigureBriefRunResponse> {
  return post<FigureBriefRunResponse>(
    `/tasks/${taskId}/figures/${fileId}/brief`,
  );
}

/**
 * Replace the whole arrangement.
 *
 * Whole rather than a move at a time, because a client that has been rearranging
 * optimistically cannot tell the server what the layout was when the drag began.
 */
export function replaceFigureLayout(
  taskId: string,
  layout: FigureLayoutRequest,
): Promise<FigureLayoutResponse> {
  return put(`/tasks/${taskId}/figures/layout`, layout);
}

/**
 * Replace the whole set of definitions.
 *
 * Whole for the same reason the layout is, and validated whole by the server: a
 * write that changed one figure's description still carries every registry row
 * and every annotation back unchanged, so the set that arrives is one the task
 * can hold.
 */
export function replaceFigureDefinitions(
  taskId: string,
  definitions: FigureDefinitionsRequest,
): Promise<FigureDefinitionsResponse> {
  return put(`/tasks/${taskId}/figures/definitions`, definitions);
}

/** The figure's formal rendered drawing, as a short-lived signed URL. */
export function getFigureDrawing(
  taskId: string,
  fileId: string,
): Promise<FigureDrawingResponse> {
  return get(`/tasks/${taskId}/figures/${fileId}/drawing`);
}

/**
 * Confirm a figure or the arrangement, or insert the confirmed set.
 *
 * A confirmation names the fingerprint or version it was built against; the
 * server refuses one aimed at state that moved on, and the caller reconciles
 * by refetching rather than by guessing.
 */
export function applyFigureReview(
  taskId: string,
  action: FigureReviewAction,
): Promise<FigureReviewState> {
  return put(`/tasks/${taskId}/figures/review`, action);
}

/**
 * Take a figure out of the set and keep its work for Restore.
 *
 * Answers the listing as it now stands, because a set aside moves the labels, the
 * definitions and the registry at once, and a client that guessed would draw the wrong set.
 */
export function setAsideFigure(
  taskId: string,
  fileId: string,
): Promise<FigureListResponse> {
  return post(`/tasks/${taskId}/figures/${fileId}/set-aside`);
}

/** Put a set-aside figure back in the set as nearly as it was, answered as the listing now stands. */
export function restoreFigure(
  taskId: string,
  fileId: string,
): Promise<FigureListResponse> {
  return post(`/tasks/${taskId}/figures/${fileId}/restore`);
}
